import { combineBots, pausingBot, type Bot } from './match';
import { BALANCED, makePlanner, type PlannerStyle } from './planner';
import { scoutBot } from './scout';
import { settlerBot } from './settler';
import { contractorBot } from './contractor';
import { researcherBot } from './researcher';
import { patronBot } from './patron';

/**
 * The phase-1 bot strategies (issue #6, GDD §15 gate). Each runs its capital
 * like the balanced planner but differs in how it treats money.
 */
export const STRATEGY_IDS = ['trader', 'hoarder', 'printer'] as const;
export type StrategyId = (typeof STRATEGY_IDS)[number];

export const STRATEGY_STYLES: Readonly<Record<StrategyId, PlannerStyle>> = {
  /**
   * Keeps lean stock, exports at almost any price and speculates on big
   * swings only: with a 10% fee and 5% conversion each way, small ones lose.
   */
  trader: {
    ...BALANCED,
    reserveCycles: 2,
    minSellRatio: 0.8,
    speculate: { buyBelow: 0.8, sellAbove: 1.25, budget: 0.1 },
  },
  /** Sits on deep stock and a fat treasury, sells only into strength. */
  hoarder: {
    ...BALANCED,
    reserveCycles: 6,
    cashReserve: 600,
    maxBuyRatio: 1.5,
    minSellRatio: 1.05,
  },
  /** Prints whatever its plans lack, until its currency falls below 0.3 ГС. */
  printer: {
    ...BALANCED,
    print: { minRate: 0.3 },
  },
};

export const STRATEGIES: Readonly<Record<StrategyId, Bot>> = {
  trader: combineBots(
    pausingBot,
    makePlanner(STRATEGY_STYLES.trader),
    scoutBot,
    settlerBot,
    contractorBot,
    researcherBot,
    patronBot,
  ),
  hoarder: combineBots(
    pausingBot,
    makePlanner(STRATEGY_STYLES.hoarder),
    scoutBot,
    settlerBot,
    contractorBot,
    researcherBot,
    patronBot,
  ),
  printer: combineBots(
    pausingBot,
    makePlanner(STRATEGY_STYLES.printer),
    scoutBot,
    settlerBot,
    contractorBot,
    researcherBot,
    patronBot,
  ),
};
